// @flow strict

// $FlowFixMe[nonstrict-import] - this is otherwise good
import {type InputAndOptionsClassNames} from 'src/components/lib/token-list-input/new-stuff.jsx';

import * as React from 'react';
import last from 'lodash/last';

// $FlowFixMe[nonstrict-import]
import {useResource} from 'src/hooks/useResource';
import logger from 'src/utils/logger';
import classify from 'src/utils/classify';

// $FlowFixMe[nonstrict-import]
import {Select} from 'src/components/lib/token-list-input';
// $FlowFixMe[nonstrict-import]
import {Select as GenesisSelect} from 'src/components/lib/token-list-input/genesis.jsx';

import css from './index.css';


export const useTzData = (): {[string]: string | string[]} => {
  const tzResource = useResource<{[string]: string | string[]}>(
    // $FlowFixMe
    () => import('./tznames.json'),
    {
      deps: [],
      onError(error) {
        logger.error('Failed to load timezones json.', error);
      },
    },
  );

  const timezones: {[string]: string | string[]} =
    tzResource.result || defaultTimezones;

  return timezones;
};

export const useTzOffsets = (tzNames: string[]): number[] => {
  const timezones = useTzData();

  const offsets: Array<number | null> = tzNames.map((tz) => {
    const entry = timezones[tz];
    if (Array.isArray(entry)) {
      return Number(entry[0]);
    } else if (typeof entry === 'string') {
      const offset = timezones[entry][0];
      if (Array.isArray(offset)) {
        return offset;
      }
    }
    return null;
  });

  // $FlowIssue[incompatible-type]: this is a number array
  const validOffsets: number[] = offsets.filter((x) => typeof x === 'number');
  return validOffsets;
};

/**
 * return the max delta in minutes between a list of standard tz names
 */
export const useTzMaxDelta = (tzNames: string[]): number => {
  const offsets = useTzOffsets(tzNames);
  // array.proto.toSorted appears in flow 0.185
  offsets.sort();
  const offsetCount = offsets.length;
  if (offsetCount > 0) {
    return offsets[offsetCount - 1] - offsets[0];
  }
  return 0;
};

export const TimezoneSelect = ({
  classNames,
  value,
  onChange,
}: {
  classNames?: InputAndOptionsClassNames,
  value: ?string,
  onChange: (tz: string) => mixed,
}): React.Node => {
  const timezones = useTzData();

  const tzLocaleCompare = (left, right) =>
    last(left.split('/')).localeCompare(last(right.split('/')));

  React.useEffect(() => {
    if (!value) {
      return;
    }
    // clean up any old timezones on mount until py migration happens
    if (!timezones[value] || Array.isArray(timezones[value])) {
      return;
    }
    if (typeof timezones[value] === 'string') {
      // update timezone
      onChange(timezones[value]);
    }
  }, [value, timezones]);

  return (
    <Select
      searchable
      classNames={classNames}
      groupOptionsBy={(tzname) => tzname.split('/')[0]}
      options={Object.keys(timezones)}
      value={value}
      placeholder="No timezone selected"
      onChange={(val) => {
        if (val) {
          onChange(Array.isArray(timezones[val]) ? val : timezones[val]);
          // dispatch(updateTemplateField(template.id, 'timezone', val));
        }
      }}
      sortOptionsBy={(left, right) => {
        // first sort by timezone offset, then by locale
        const tzOffsetDelta =
          Number(timezones[left][0]) - Number(timezones[right][0]);
        return tzOffsetDelta + tzLocaleCompare(left, right);
      }}
      resolveLabel={(tzname) => labelForTimezone(tzname, timezones)}
      resolveKey={(tzname) => tzname}
    />
  );
};

export const NewTimezoneSelect = ({
  classNames,
  value,
  onChange,
}: {
  classNames?: InputAndOptionsClassNames,
  value: ?string,
  onChange: (tz: string) => mixed,
}): React.Node => {
  const tzResource = useResource<{[string]: string | string[]}>(
    // $FlowFixMe
    () => import('./tznames.json'),
    {
      deps: [],
      onError(error) {
        logger.error('Failed to load timezones json.', error);
      },
    },
  );

  const timezones: {[string]: string | string[]} =
    tzResource.result || defaultTimezones;

  const tzLocaleCompare = (left, right) =>
    last(left.split('/')).localeCompare(last(right.split('/')));

  React.useEffect(() => {
    if (!value) {
      return;
    }
    // clean up any old timezones on mount until py migration happens
    if (!timezones[value] || Array.isArray(timezones[value])) {
      return;
    }
    if (typeof timezones[value] === 'string') {
      // update timezone
      onChange(timezones[value]);
    }
  }, [value, timezones]);

  // we allow callers of this component to pass in their own classes here
  // which build on the existing styles
  const {box: callerBoxCss, ...restClasses} = classNames ?? {};

  return (
    <Select
      searchable
      classNames={{
        ...restClasses,
        box: classify(css.tzBox, callerBoxCss),
      }}
      groupOptionsBy={(tzname) => tzname.split('/')[0]}
      options={Object.keys(timezones)}
      value={value}
      placeholder="No timezone selected"
      onChange={(val) => {
        if (val) {
          onChange(Array.isArray(timezones[val]) ? val : timezones[val]);
        }
      }}
      sortOptionsBy={(left, right) => {
        // first sort by timezone offset, then by locale
        const tzOffsetDelta =
          Number(timezones[left][0]) - Number(timezones[right][0]);
        return tzOffsetDelta + tzLocaleCompare(left, right);
      }}
      resolveLabel={(tzname) => labelForTimezone(tzname, timezones)}
      resolveKey={(tzname) => tzname}
    />
  );
};

export const GenesisTZPicker = ({
  classNames,
  value,
  onChange,
  customTimezones,
  excludedTimezones,
}: {
  classNames?: InputAndOptionsClassNames,
  value: ?string,
  onChange: (tz: string) => mixed,
  customTimezones?: {[string]: string | Array<string | null>},
  excludedTimezones?: string[],
}): React.Node => {
  const tzResource = useResource<{[string]: string | string[]}>(
    // $FlowFixMe
    () => import('./tznames.json'),
    {
      deps: [],
      onError(error) {
        logger.error('Failed to load timezones json.', error);
      },
    },
  );

  const timezones: {[string]: string | string[]} = React.useMemo(() => {
    const baseTimezones = tzResource.result || defaultTimezones;
    return customTimezones || excludedTimezones
      ? prepareTimezones(
          baseTimezones,
          customTimezones || {},
          excludedTimezones || [],
        )
      : baseTimezones;
  }, [tzResource.result, customTimezones, excludedTimezones]);

  const tzLocaleCompare = (left, right) =>
    last(left.split('/')).localeCompare(last(right.split('/')));

  React.useEffect(() => {
    if (!value) {
      return;
    }
    // clean up any old timezones on mount until py migration happens
    if (!timezones[value] || Array.isArray(timezones[value])) {
      return;
    }
    if (typeof timezones[value] === 'string') {
      // update timezone
      onChange(timezones[value]);
    }
  }, [value, timezones]);

  // we allow callers of this component to pass in their own classes here
  // which build on the existing styles
  const {box: callerBoxCss, ...restClasses} = classNames ?? {};

  return (
    <GenesisSelect
      searchable
      classNames={{
        ...restClasses,
        box: classify(css.tzBox, callerBoxCss),
      }}
      groupOptionsBy={(tzname) => tzname.split('/')[0]}
      options={Object.keys(timezones)}
      value={value}
      placeholder="No timezone selected"
      onChange={(val) => {
        if (val) {
          onChange(Array.isArray(timezones[val]) ? val : timezones[val]);
        }
      }}
      sortOptionsBy={(left, right) => {
        // first sort by timezone offset, then by locale
        const tzOffsetDelta =
          Number(timezones[left][0]) - Number(timezones[right][0]);
        return tzOffsetDelta + tzLocaleCompare(left, right);
      }}
      resolveLabel={(tzname) => labelForTimezone(tzname, timezones)}
      resolveKey={(tzname) => tzname}
    />
  );
};

const offsetRe = /^[\+\-]/;

const offsetFromMinutes = (minuteOffsetString) => {
  const absMinuteOffset = Math.abs(Number(minuteOffsetString));
  const hSign = -1 * Math.sign(Number(minuteOffsetString));
  const h = Math.floor(absMinuteOffset / 60);
  const m = absMinuteOffset % 60;
  return h === 0 && m === 0
    ? ''
    : `${hSign > 0 ? '+' : '-'}${String(h).padStart(2, '0')}:${String(
        m,
      ).padStart(2, '0')}`;
};

function labelForTimezone(
  tzname: string,
  timezones: {[string]: string | string[]},
) {
  const basicOffset =
    typeof timezones[tzname] === 'string' &&
    Array.isArray(timezones[timezones[tzname]])
      ? timezones[timezones[tzname]]
      : timezones[tzname];
  let offset;

  if (Array.isArray(basicOffset)) {
    if (basicOffset[2].includes('%s')) {
      // the %s is either standard/daylight, force standard time for now
      offset = `${basicOffset[2].replace('%s', 'S')} ${offsetFromMinutes(
        basicOffset[0],
      )}`.trim();
    } else if (offsetRe.test(basicOffset[2])) {
      // starts with an +- offset, just parse
      // the minutes instead, more reliable
      offset = offsetFromMinutes(basicOffset[0]);
    } else {
      // use the UTC/GMT non-%s string
      offset = `${basicOffset[2]} ${offsetFromMinutes(basicOffset[0])}`.trim();
    }
  }

  return `${tzname.replace('_', ' ')}${offset ? ' (' + offset + ')' : ''}`;
}

const defaultTimezones = {
  'America/Denver': ['420', 'US', 'M%sT'],
  'America/Los_Angeles': ['480', 'US', 'P%sT'],
  'America/New_York': ['300', 'US', 'E%sT'],
  'America/Chicago': ['360', 'US', 'C%sT'],
};

const prepareTimezones = (
  timezones: {[string]: string | string[]},
  customTimezones: {[string]: string | Array<string | null>} = {},
  excludedTimezones: string[] = [],
): {[string]: string | string[]} => {
  // Apply updates
  // $FlowFixMe[incompatible-type]
  const timezonesObject = {...timezones, ...customTimezones};

  // Remove excluded timezones
  if (Array.isArray(excludedTimezones)) {
    excludedTimezones.forEach((key) => {
      if (key in timezonesObject) {
        delete timezonesObject[key];
      }
    });
  }

  return timezonesObject;
};
